import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { isArray } from 'lodash';
import { Form, Accordion, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import { format } from 'date-fns';
import Button from '../ui/Button';
import Loader from '../ui/Loader';
import { allowDropHandler, showMetaEditTool } from '../../utils/uiUtils';
import { SearchParams } from '../../utils/searchUtils';
import { metaAttach } from '../../actions/metaAttach';
import { stringifiedToDraggable } from '../../utils/draggables';
import {
  META_ATTACH_STATE_FAILURE,
  META_ATTACH_STATE_IN_PROGRESS,
} from '../../constants/metaAttachStates';
import { deselectAll } from '../../actions/umoGallery';
import appConfig from '../../appConfig';
import { DATE_FORMAT } from '../../constants/session';

/* eslint-disable no-use-before-define */
function MassMetaUpdate({
  intl,
  dispatch,
  metaAttachState,
  metadataToAttach,
  selectedUMOIDs,
  searchParams,
  isSelectAll,
  selectedLanguage,
}) {
  const [isReplace, setReplace] = useState(false);

  const attachMetadata = (umoIDList, params) => {
    dispatch(metaAttach(
      metadataToAttach,
      umoIDList,
      isReplace,
      params,
      intl,
    ))
      .then(() => dispatch(deselectAll()));
  };

  const onAttachSelectedClick = () => {
    attachMetadata(selectedUMOIDs.toJS(),
      isSelectAll ? searchParams : null);
  };

  const getMetatagVal = (value) => {
    if (isArray(value)) {
      if (isArray(value[0])) { // repeating group
        const dispValue = [];
        value.forEach((repMeta) => dispValue.push(parseMetatag(repMeta)));
        return <ul className="meta-attach__cur-inner-list">{dispValue}</ul>;
      } // single repeating field
      return (
        <ul className="meta-attach__cur-inner-list">
          {parseMetatag(value)}
        </ul>
      );
    } return value;
  };

  const parseMetatag = (tags) => {
    const metas = [];
    let tagValue;
    tags.forEach(meta => {
      tagValue = (meta.type === 'DATE' && meta.value) ? format(meta.value, appConfig.dateFormat || DATE_FORMAT) : meta.value;
      metas.push(
        <li className="meta-attach__cur-list-item" key={`mt-edt-${meta.id}`}>
          {meta.label} - {getMetatagVal(tagValue)}
        </li>);
        });

    return metas;
  };

  const editMetadata = () => {
    const metaPatams = JSON.stringify(metadataToAttach);
    sessionStorage.setItem('massMetaEditVals', metaPatams);
    showMetaEditTool(null, dispatch);
  };

  const dropHandler = (e) => {
    e.preventDefault();
    const draggable = stringifiedToDraggable(e.dataTransfer.getData('text'));
    if (!(draggable instanceof Error)) {
      attachMetadata(
        isArray(draggable.getSelectionList()) ?
          draggable.getSelectionList() : [],
        draggable.getIsSelectAll() ? draggable.getSearchParams() : null
      );
    }
  };

  const isLoading = metaAttachState === META_ATTACH_STATE_IN_PROGRESS;
  let isDisabled = false;
  const metatags = parseMetatag(metadataToAttach);
  const editMetaTT =
      (<Tooltip id="meta-attach__edit-values-tooltip">
        {intl.formatMessage({ id: 'metaAttach.editCurValues' })}
      </Tooltip>);

  if (isLoading ||
      (isSelectAll && !searchParams) ||
      (!isSelectAll && selectedUMOIDs.count() < 1)) {
    isDisabled = true;
  }

  return (
    <div className="meta-attach__mass-update meta-attach__drop-area">
      <div
        className='meta-attach__controls-container'
        onDrop={dropHandler}
        onDragOver={isLoading ? undefined : allowDropHandler}
      >
        <div style={{ fontSize: '20px' }}>{intl.formatMessage({ id: 'navAdminPanel.metaAttach' })}</div>
        { // don't display the drop area on IE browsers
          navigator.msSaveBlob ? null :
            <div className='meta-attach__drop-inner'>
              <i className='meta-attach__drop-inner-icon' />
              <p>{intl.formatMessage({ id: 'metaAttach.attachArea' })}</p>
            </div>
        }
        <Button
          primary
          block
          disabled={isDisabled}
          onClick={onAttachSelectedClick}
          className='meta-attach__submit-btn'
        >
          {isLoading ? <Loader /> : intl.formatMessage({ id: 'metaAttach.attachButton' })}
        </Button>
        {appConfig.enableMultiLang && selectedLanguage ?
        <div className='meta-attach__language-help-text'>
          Note: Values will be saved for fields in: {selectedLanguage.$ ? selectedLanguage.$.display : selectedLanguage}</div>
          : null }
        {metaAttachState === META_ATTACH_STATE_FAILURE ?
          <div className='invalid-feedback' style={{ display: 'block' }}>
            {intl.formatMessage({ id: 'general.error' })}
          </div> : null
        }
        <Form.Group className='meta-attach__controls-container__replace-check'>
          <Form.Check
            type='checkbox'
            id='meta-attach__replace-check'
            onChange={() => setReplace(!isReplace)}
            checked={isReplace}
            label={intl.formatMessage({ id: 'metaAttach.replaceCheckbox' })}
          />
          <Form.Text className='text-muted meta-attach__help-text'>
            {intl.formatMessage({ id: 'metaAttach.defaultAttachBehavior' })}
          </Form.Text>
        </Form.Group>
        <Accordion>
          <Accordion.Item eventKey="0">
            <Accordion.Header>
                <div className="meta-attach__metadata">
                <OverlayTrigger overlay={editMetaTT} placement='bottom'>
                <Button className="meta-attach__edit" variant='link' onClick={editMetadata}>
                  <i className='fas fa-pencil-alt meta-attach__edit-values' />
                </Button>
              </OverlayTrigger>
                  {intl.formatMessage({ id: 'metaAttach.currentValues' })}
                </div>
            </Accordion.Header>
              <Accordion.Body>
                <ul className='meta-attach__cur-list'>{metatags}</ul>
              </Accordion.Body>
            </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
}

MassMetaUpdate.propTypes = {
  metaAttachState: PropTypes.string,
  metadataToAttach: PropTypes.oneOfType([
    PropTypes.instanceOf(List),
    PropTypes.instanceOf(Array),
  ]),
  dispatch: PropTypes.func,
  selectedUMOIDs: PropTypes.instanceOf(List),
  searchParams: PropTypes.instanceOf(SearchParams),
  isSelectAll: PropTypes.bool,
};

export default injectIntl(MassMetaUpdate);
